<script setup>
import Logo from '@/layouts/full/logo/LogoLight.vue';
import AuthLogin from '../authForms/AuthLogin.vue';
import AuthRegister from '../authForms/AuthRegister.vue';
import AuthForgotPwd from '../authForms/AuthForgotPwd.vue';
import { ref } from 'vue';

// Track the current form to display
const currentForm = ref('login'); // 'login', 'register', or 'forgot'

const switchToLogin = () => {
  currentForm.value = 'login';
};

const switchToRegister = () => {
  currentForm.value = 'register';
};

const switchToForgotPassword = () => {
  currentForm.value = 'forgot';
};

// Note: The reCAPTCHA integration was removed for cleaner production UI
// If you need to re-enable it, add the following:
/*
import { createApp } from 'vue';
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3';

const component = {
  setup() {
    const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

    const recaptcha = async () => {
      await recaptchaLoaded();
      await executeRecaptcha('login');
    };

    return { recaptcha };
  },
  template: '<button @click="recaptcha">Execute recaptcha</button>'
};

createApp(component).use(VueReCaptcha, { siteKey: '6LeCprcaAAAAAOD0aEK7WpfHc__CyRmk3rD-otNt' });
*/
</script>

<template>
  <div class="login-wrapper">
    <div class="login-content">
      <!-- Brand panel -->
      <div class="brand-panel">
        <div class="brand-container">
          <Logo class="brand-logo" />
        </div>
      </div>
      
      <!-- Form panel -->
      <div class="form-panel">
        <div class="form-container">
          <!-- Login Form -->
          <div v-if="currentForm === 'login'">
            <div class="form-header">
              <h1 class="main-heading">Sign In</h1>
              <p class="sub-heading">Access your Voxiis account</p>
            </div>
            
            <AuthLogin @forgot-password="switchToForgotPassword" />
            
            <div class="form-footer">
              <p class="copyright">© {{ new Date().getFullYear() }} Voxiis. All rights reserved.</p>
              <div class="mt-4 text-center">
                <p class="text-medium-emphasis">Don't have an account yet? 
                  <a @click="switchToRegister" class="form-link">Register</a>
                </p>
              </div>
            </div>
          </div>
          
          <!-- Register Form -->
          <div v-else-if="currentForm === 'register'">
            <div class="form-header">
              <h1 class="main-heading">Create Account</h1>
              <p class="sub-heading">Join Voxiis and start managing your services</p>
            </div>
            
            <AuthRegister />
            
            <div class="form-footer">
              <p class="copyright">© {{ new Date().getFullYear() }} Voxiis. All rights reserved.</p>
              <div class="mt-4 text-center">
                <p class="text-medium-emphasis">Already have an account? 
                  <a @click="switchToLogin" class="form-link">Sign In</a>
                </p>
              </div>
            </div>
          </div>
          
          <!-- Forgot Password Form -->
          <div v-else-if="currentForm === 'forgot'">
            <div class="form-header">
              <h1 class="main-heading">Forgot Password?</h1>
              <p class="sub-heading">Reset your Voxiis account password</p>
            </div>
            
            <AuthForgotPwd @switch-to-login="switchToLogin" />
            
            <div class="form-footer">
              <p class="copyright">© {{ new Date().getFullYear() }} Voxiis. All rights reserved.</p>
              <div class="mt-4 text-center">
                <p class="text-medium-emphasis">Remember your password? 
                  <a @click="switchToLogin" class="form-link">Sign In</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.login-wrapper {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
}

.login-content {
  display: flex;
  width: 90%;
  max-width: 1200px;
  height: 85vh;
  max-height: 800px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 95%;
    height: auto;
    min-height: 85vh;
    flex-direction: column;
  }
}

.brand-panel {
  flex: 0 0 45%;
  background-color: #243a70;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 70%);
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    flex: 0 0 120px;
    width: 100%;
  }
}

.brand-container {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 2rem;
  
  .brand-logo {
    width: 180px;
    height: auto;
    margin-bottom: 1rem;
  }
}

.form-panel {
  flex: 1;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
}

.form-container {
  width: 100%;
  max-width: 500px;
  padding: 0 2rem;
}

.form-header {
  margin-bottom: 2.5rem;
  
  .main-heading {
    font-size: 2rem;
    font-weight: 700;
    color: #243a70;
    margin-bottom: 0.5rem;
    letter-spacing: -0.5px;
  }
  
  .sub-heading {
    font-size: 1rem;
    color: #64748b;
    font-weight: normal;
  }
}

.form-footer {
  margin-top: 2rem;
  text-align: center;
  
  .copyright {
    font-size: 0.8rem;
    color: #94a3b8;
  }
}

.form-link {
  color: #243a70;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
}
</style>
